// Same placement as the homepage galleries: each photograph goes into the
// shortest column, using its height/width ratio. Columns stay filled and the
// gap between photographs is 14px.

export function packShortestColumns<T>(items: T[], aspects: number[], columns: number): T[][] {
  if (!items.length) return [];
  const count = Math.max(1, columns);
  const packed: T[][] = Array.from({ length: count }, () => []);
  const heights = new Array(count).fill(0);
  items.forEach((item, index) => {
    let shortest = 0;
    for (let column = 1; column < count; column += 1) {
      if (heights[column] < heights[shortest]) shortest = column;
    }
    packed[shortest].push(item);
    heights[shortest] += aspects[index] > 0 ? aspects[index] : 0.67;
  });
  return packed;
}

export function masonryColumnCount(chosen: number, width: number): number {
  const columns = chosen >= 1 && chosen <= 5 ? Math.round(chosen) : 3;
  if (width <= 639) return 1;
  if (width <= 1023) return Math.min(columns, 2);
  return columns;
}

function chosenColumns(block: HTMLElement): number {
  const match = block.className.match(/\bphoto-masonry-([1-5])\b/);
  const columns = Number(match?.[1]);
  return columns >= 1 && columns <= 5 ? columns : 3;
}

function blockFigures(block: HTMLElement): HTMLElement[] {
  const nodes = block.querySelectorAll('figure');
  const figures: HTMLElement[] = [];
  for (let index = 0; index < nodes.length; index += 1) {
    const figure = nodes[index];
    if (figure.closest('.photo-masonry') === block) figures.push(figure);
  }
  return figures;
}

function figureAspect(figure: HTMLElement): number {
  const img = figure.querySelector('img');
  const width = (img instanceof HTMLImageElement && img.naturalWidth) || Number(img?.getAttribute('width')) || 0;
  const height = (img instanceof HTMLImageElement && img.naturalHeight) || Number(img?.getAttribute('height')) || 0;
  const aspect = width > 0 && height > 0 ? height / width : 0.67;
  const caption = figure.querySelector('figcaption');
  return aspect + (caption && caption.textContent?.trim() ? 0.12 : 0);
}

export function flattenPhotoMasonry(root: ParentNode): void {
  root.querySelectorAll('.photo-masonry').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const figures = blockFigures(node);
    figures.forEach((figure) => node.appendChild(figure));
    node.querySelectorAll('.photo-masonry-col').forEach((column) => column.remove());
    node.classList.remove('is-packed');
    node.style.removeProperty('--photo-masonry-cols');
    delete node.dataset.pack;
  });
}

export function layoutPhotoMasonry(root: ParentNode, options?: { simulatePreview?: boolean }): void {
  root.querySelectorAll('.photo-masonry').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const figures = blockFigures(node);
    if (!figures.length) return;
    const width = node.getBoundingClientRect().width;
    if (width < 40) return;
    const chosen = chosenColumns(node);
    const columns = options?.simulatePreview ? chosen : masonryColumnCount(chosen, width);
    const aspects = figures.map(figureAspect);
    const signature = `${columns}:${Math.round(width / 8)}:${aspects.map((aspect) => aspect.toFixed(3)).join(',')}`;
    if (node.dataset.pack === signature && node.classList.contains('is-packed')) return;
    figures.forEach((figure) => node.appendChild(figure));
    node.querySelectorAll('.photo-masonry-col').forEach((column) => column.remove());
    node.style.setProperty('--photo-masonry-cols', String(columns));
    // Skip empty columns so a short row (e.g. 3 photos in 5 columns) can center.
    packShortestColumns(figures, aspects, columns).forEach((group) => {
      if (!group.length) return;
      const column = document.createElement('div');
      column.className = 'photo-masonry-col';
      column.setAttribute('contenteditable', 'false');
      group.forEach((figure) => column.appendChild(figure));
      node.appendChild(column);
    });
    node.classList.add('is-packed');
    node.dataset.pack = signature;
  });
}

export function bindPhotoMasonry(root: ParentNode, options?: { simulatePreview?: boolean }): () => void {
  let frame = 0;
  const run = () => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => layoutPhotoMasonry(root, options));
  };
  layoutPhotoMasonry(root, options);
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(run);
  root.querySelectorAll('.photo-masonry').forEach((node) => observer?.observe(node));
  const onLoad = (event: Event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement && target.closest('.photo-masonry')) run();
  };
  root.addEventListener('load', onLoad, true);
  return () => {
    window.cancelAnimationFrame(frame);
    observer?.disconnect();
    root.removeEventListener('load', onLoad, true);
  };
}
